import { getClientReports, getReportTypes } from "@/ts/retrieve-data";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
    AdGroup,
    Campaign,
    IReportData,
    IReportsData,
    ITagTypeAggregate,
    Keyword,
    Portfolio,
    ProductAd,
    TGoal,
    TMediaPlanEditConfig,
    TMediaPlanList,
} from "@ts/common-types";
import { GridApi } from "ag-grid-community";

const portfoliosSlice = createSlice({
    name: "portfoliosData",
    initialState: { value: [] },
    reducers: {
        setPortfoliosData: (state, action: PayloadAction<Portfolio[]>) => {
            state.value = action.payload;
        },
    },
});

const campaignsSlice = createSlice({
    name: "campaignsData",
    initialState: { value: [] },
    reducers: {
        setCampaignsData: (state, action: PayloadAction<Campaign[]>) => {
            state.value = action.payload;
        },
    },
});

//get all unique tags added for a client per module
const recordTagsDataSlice = createSlice({
    name: "recordTagsData",
    initialState: { value: [] },
    reducers: {
        updateRecordTagsData: (state, action: PayloadAction<string[]>) => {
            state.value = action.payload;
        },
    },
});

const adgroupsSlice = createSlice({
    name: "adgroupsData",
    initialState: { value: [] },
    reducers: {
        setAdgroupsData: (state, action: PayloadAction<AdGroup[]>) => {
            state.value = action.payload;
        },
    },
});

const keywordsSlice = createSlice({
    name: "keywordsData",
    initialState: { value: [] },
    reducers: {
        setKeywordsData: (state, action: PayloadAction<Keyword[]>) => {
            state.value = action.payload;
        },
    },
});

const manualRecommendationSlice = createSlice({
    name: "manualRecommendationData",
    initialState: { value: [] },
    reducers: {
        setManualRecommendationData: (state, action: PayloadAction<Keyword[]>) => {
            state.value = action.payload;
        },
    },
});

const autoRecommendationSlice = createSlice({
    name: "autoRecommendationData",
    initialState: { value: [] },
    reducers: {
        setAutoRecommendationData: (state, action: PayloadAction<Keyword[]>) => {
            state.value = action.payload;
        },
    },
});

const revinitiRecommendationSlice = createSlice({
    name: "revinitiRecommendationData",
    initialState: { value: [] },
    reducers: {
        setRevinitiRecommendationData: (state, action: PayloadAction<Keyword[]>) => {
            state.value = action.payload;
        },
    },
});

const budgetRecommendationSlice = createSlice({
    name: "budgetRecommendationData",
    initialState: { value: [] },
    reducers: {
        setBudgetRecommendationData: (state, action: PayloadAction<Keyword[]>) => {
            state.value = action.payload;
        },
    },
});

const placementRecommendationSlice = createSlice({
    name: "placementRecommendationData",
    initialState: { value: [] },
    reducers: {
        setPlacementRecommendationData: (state, action: PayloadAction<Keyword[]>) => {
            state.value = action.payload;
        },
    },
});

const placementsSlice = createSlice({
    name: "placementsData",
    initialState: { value: [] },
    reducers: {
        setPlacementsData: (state, action: PayloadAction<Keyword[]>) => {
            state.value = action.payload;
        },
    },
});

const productsSlice = createSlice({
    name: "productsData",
    initialState: { value: [] },
    reducers: {
        setProductsData: (state, action: PayloadAction<ProductAd[]>) => {
            state.value = action.payload;
        },
    },
});

const productAnalyticsDataSlice = createSlice({
    name: "productAnalyticsData",
    initialState: { value: [] },
    reducers: {
        setProductAnalyticsData: (state, action: PayloadAction<ProductAd[]>) => {
            state.value = action.payload;
        },
    },
});

const createKeywordsReviewDataSlice = createSlice({
    name: "createKeywordsReviewData",
    initialState: { value: [] },
    reducers: {
        setCreateKeywordsReviewData: (
            state,
            action: PayloadAction<Record<string, string | number | string[]>[]>
        ) => {
            state.value = action.payload;
        }
    }
})

const createKeywordsDataSlice = createSlice({
    name: "createKeywordsData",
    initialState: { value: [] },
    reducers: {
        setCreateKeywordsData: (
            state,
            action: PayloadAction<Record<string, string | number>[]>
        ) => {
            state.value = action.payload;
        }
    }
});

const reportsDataSlice = createSlice({
    name: "reportsData",
    initialState: {
        value: {
            revstack: [],
            common: [],
        },
        isLoading: false,
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            getClientReports.pending,
            (state: IReportData) => {
                state.isLoading = true;
            }
        );
        builder.addCase(
            getClientReports.fulfilled,
            (state: IReportData, action) => {
                state.isLoading = false;
                const revstackReports = action.payload ? action.payload.filter(
                    (report: IReportsData) => report.report_type_id === 1
                ) : [];
                const commonReports = action.payload ? action.payload.filter(
                    (report: IReportsData) => report.report_type_id === 2
                ) : [];
                state.value = {
                    revstack: revstackReports,
                    common: commonReports,
                };
            }
        );
        builder.addCase(
            getClientReports.rejected,
            (state: IReportData, action) => {
                state.isLoading = false;
                state.error = action.error.message;
            }
        );
    }
});

const reportTypesDataSlice = createSlice({
    name: "reportTypesData",
    initialState: {
        value: [],
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            getReportTypes.fulfilled,
            (state, action) => {
                state.value = action.payload;
            }
        );
    }
});

const selectedReportDataSlice = createSlice({
    name: "selectsedReportData",
    initialState: { value: {} },
    reducers: {
        setSelectedReportData: (
            state,
            action: PayloadAction<IReportsData>
        ) => {
            state.value = action.payload;
        }
    }
});

const suggestedNegKwsDataSlice = createSlice({
    name: "suggestedNegKwsData",
    initialState: { value: [] },
    reducers: {
        setSuggestedNegKwsData: (
            state,
            action: PayloadAction<Keyword[]>
        ) => {
            state.value = action.payload;
        }
    }
});

export const suggestedNegKwsLoadingSlice = createSlice({
    name: "suggestedNegKwsLoading",
    initialState: { value: false },
    reducers: {
        setSuggestedNegKwsLoading: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.value = action.payload;
        }
    }
});

const existingNegKwsDataSlice = createSlice({
    name: "existingNegKwsData",
    initialState: { value: [] },
    reducers: {
        setExistingNegKwsData: (
            state,
            action: PayloadAction<Keyword[]>
        ) => {
            state.value = action.payload;
        }
    }
});

export const existingNegKwsLoadingSlice = createSlice({
    name: "existingNegKwsLoading",
    initialState: { value: false },
    reducers: {
        setExistingNegKwsLoading: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.value = action.payload;
        }
    }
});

const showDeltaValuesSlice = createSlice({
    name: "showDeltaValues",
    initialState: { value: true },
    reducers: {
        updateShowDeltaValues: (state, action: PayloadAction<boolean>) => {
            state.value = action.payload;
        },
    },
});

const goalDataSlice = createSlice({
    name: "goalData",
    initialState: { value: [] },
    reducers: {
        setGoalData: (state, action: PayloadAction<TGoal[]>) => {
            state.value = action.payload;
        },
    },
});

const tagTypeAggregatedDataSlice = createSlice({
    name: "tagTypeAggregatedData",
    initialState: { value: [] },
    reducers: {
        setTagTypeAggregatedData: (state, action: PayloadAction<ITagTypeAggregate[]>) => {
            state.value = action.payload;
        },
    },
});

const tagTypeOptionsSlice = createSlice({
    name: "tagTypeOptions",
    initialState: { value: [] },
    reducers: {
        setTagTypeOptions: (state, action: PayloadAction<string[]>) => {
            state.value = action.payload;
        },
    },
});

const selectedTagTypeOptionSlice = createSlice({
    name: "selectedTagTypeOption",
    initialState: { value: "" },
    reducers: {
        setSelectedTagTypeOption: (state, action: PayloadAction<string>) => {
            state.value = action.payload;
        },
    },
});

const campaignLevelTotalSpendSlice = createSlice({
    name: "campaignLevelTotalSpend",
    initialState: { value: 0 },
    reducers: {
        setCampaignLevelTotalSpend: (state, action: PayloadAction<number>) => {
            state.value = action.payload;
        },
    },
});

const tagTypeLevelTotalSpendSlice = createSlice({
    name: "tagTypeLevelTotalSpend",
    initialState: { value: 0 },
    reducers: {
        setTagTypeLevelTotalSpend: (state, action: PayloadAction<number>) => {
            state.value = action.payload;
        },
    },
});

const funnelResponseSlice = createSlice({
    name: "funnelResponse",
    initialState: { value: {} },
    reducers: {
        setFunnelResponse: (state, action: PayloadAction<Record<string, string | number | boolean>>) => {
            state.value = action.payload;
        },
    },
});

const productMappingDataSlice = createSlice({
    name: "productMappingData",
    initialState: { value: [] },
    reducers: {
        setProductMappingData: (state, action: PayloadAction<any[]>) => {
            state.value = action.payload;
        },
    },
});

const generateMediaPlanDataSlice = createSlice({
    name: "generateMediaPlanData",
    initialState: { value: {} },
    reducers: {
        setGenerateMediaPlanData: (state, action: PayloadAction<any>) => {
            state.value = action.payload;
        },
    },
});

const mediaPlanBudgetSlice = createSlice({
    name: "mediaPlanBudget",
    initialState: { value: 0 },
    reducers: {
        setMediaPlanBudget: (state, action: PayloadAction<number>) => {
            state.value = action.payload;
        },
    },
})

const mediaPlanGridApiMapSlice = createSlice({
    name: "mediaPlanGridApiMap",
    initialState: { value: {} },
    reducers: {
        setMediaPlanGridApiMap: (
            state,
            action: PayloadAction<Record<string, GridApi>>
        ) => {
            state.value = {
                ...state.value,
                ...action.payload
            };
        },
        resetMediaPlanGridApiMap: (
            state,
            action: PayloadAction<Record<string, GridApi>>
        ) => {
            state.value = {};
        }
    },
});

const mediaPlanEditConfigSlice = createSlice({
    name: "mediaPlanEditConfig",
    initialState: { value: {} },
    reducers: {
        setMediaPlanEditConfig: (
            state,
            action: PayloadAction<TMediaPlanEditConfig>
        ) => {
            state.value = {
                ...state.value,
                ...action.payload
            };
        },
        resetMediaPlanEditConfig: (
            state,
            action: PayloadAction<TMediaPlanEditConfig>
        ) => {
            state.value = {};
        },
    },
});

const mediaPlanningListSlice = createSlice({
    name: "mediaPlanningList",
    initialState: { value: [] },
    reducers: {
        setMediaPlanningList: (
            state,
            action: PayloadAction<TMediaPlanList[]>
        ) => {
            state.value = action.payload;
        },
    },
});

const isMediaPlanEditSlice = createSlice({
    name: "isMediaPlanEdit",
    initialState: { value: false },
    reducers: {
        setIsMediaPlanEdit: (state, action: PayloadAction<boolean>) => {
            state.value = action.payload;
        },
    },
});

const isMediaPlanCreateSlice = createSlice({
    name: "isMediaPlanCreate",
    initialState: { value: false },
    reducers: {
        setIsMediaPlanCreate: (state, action: PayloadAction<boolean>) => {
            state.value = action.payload;
        },
    },
});

const showMediaPlanChangeLogsSlice = createSlice({
    name: "showMediaPlanChangeLogs",
    initialState: { value: false },
    reducers: {
        setShowMediaPlanChangeLogs: (state, action: PayloadAction<boolean>) => {
            state.value = action.payload;
        },
    },
})

export const { setPortfoliosData } = portfoliosSlice.actions;
export const { setCampaignsData } = campaignsSlice.actions;
export const { updateRecordTagsData } = recordTagsDataSlice.actions;
export const { setAdgroupsData } = adgroupsSlice.actions;
export const { setKeywordsData } = keywordsSlice.actions;
export const { setProductsData } = productsSlice.actions;
export const { setCreateKeywordsReviewData } = createKeywordsReviewDataSlice.actions;
export const { setCreateKeywordsData } = createKeywordsDataSlice.actions;
export const { setManualRecommendationData } = manualRecommendationSlice.actions;
export const { setAutoRecommendationData } = autoRecommendationSlice.actions;
export const { setRevinitiRecommendationData } = revinitiRecommendationSlice.actions;
export const { setBudgetRecommendationData } = budgetRecommendationSlice.actions;
export const { setPlacementRecommendationData } = placementRecommendationSlice.actions;
export const { setPlacementsData } = placementsSlice.actions;
export const { setProductAnalyticsData } = productAnalyticsDataSlice.actions;
export const { setSelectedReportData } = selectedReportDataSlice.actions;
export const { updateShowDeltaValues } = showDeltaValuesSlice.actions;
export const { setGoalData } = goalDataSlice.actions;
export const { setSuggestedNegKwsData } = suggestedNegKwsDataSlice.actions;
export const { setSuggestedNegKwsLoading } = suggestedNegKwsLoadingSlice.actions;
export const { setExistingNegKwsData } = existingNegKwsDataSlice.actions;
export const { setExistingNegKwsLoading } = existingNegKwsLoadingSlice.actions;
export const { setTagTypeAggregatedData } = tagTypeAggregatedDataSlice.actions;
export const { setTagTypeOptions } = tagTypeOptionsSlice.actions;
export const { setSelectedTagTypeOption } = selectedTagTypeOptionSlice.actions;
export const { setCampaignLevelTotalSpend } = campaignLevelTotalSpendSlice.actions;
export const { setTagTypeLevelTotalSpend } = tagTypeLevelTotalSpendSlice.actions;
export const { setFunnelResponse } = funnelResponseSlice.actions;
export const { setProductMappingData } = productMappingDataSlice.actions;
export const { setGenerateMediaPlanData } = generateMediaPlanDataSlice.actions;
export const { setMediaPlanGridApiMap, resetMediaPlanGridApiMap } = mediaPlanGridApiMapSlice.actions;
export const { setMediaPlanningList } = mediaPlanningListSlice.actions;
export const { setMediaPlanBudget } = mediaPlanBudgetSlice.actions;
export const { setMediaPlanEditConfig, resetMediaPlanEditConfig } = mediaPlanEditConfigSlice.actions;
export const { setIsMediaPlanEdit } = isMediaPlanEditSlice.actions;
export const { setIsMediaPlanCreate } = isMediaPlanCreateSlice.actions;
export const { setShowMediaPlanChangeLogs } = showMediaPlanChangeLogsSlice.actions;

export const portfoliosReducer = portfoliosSlice.reducer;
export const campaignsReducer = campaignsSlice.reducer;
export const recordTagsDataReducer = recordTagsDataSlice.reducer;
export const adgroupsReducer = adgroupsSlice.reducer;
export const keywordReducer = keywordsSlice.reducer;
export const productsReducer = productsSlice.reducer;
export const createKeywordsReviewDataReducer = createKeywordsReviewDataSlice.reducer;
export const createKeywordsDataReducer = createKeywordsDataSlice.reducer;
export const manualRecommendationReducer = manualRecommendationSlice.reducer;
export const autoRecommendationReducer = autoRecommendationSlice.reducer;
export const revinitiRecommendationReducer = revinitiRecommendationSlice.reducer;
export const budgetRecommendationReducer = budgetRecommendationSlice.reducer;
export const placementRecommendationReducer = placementRecommendationSlice.reducer;
export const placementsReducer = placementsSlice.reducer;
export const productAnalyticsDataReducer = productAnalyticsDataSlice.reducer;

export const reportsDataReducer = reportsDataSlice.reducer;
export const reportTypesDataReducer = reportTypesDataSlice.reducer;
export const selectedReportDataReducer = selectedReportDataSlice.reducer;
export const showDeltaValuesReducer = showDeltaValuesSlice.reducer

export const suggestedNegKwsDataReducer = suggestedNegKwsDataSlice.reducer;
export const suggestedNegKwsLoadingReducer = suggestedNegKwsLoadingSlice.reducer;
export const exstingNegKwsDataReducer = existingNegKwsDataSlice.reducer;
export const existingNegKwsLoadingReducer = existingNegKwsLoadingSlice.reducer;
export const goalDataReducer = goalDataSlice.reducer;
export const tagTypeAggregatedDataReducer = tagTypeAggregatedDataSlice.reducer;
export const tagTypeOptionsReducer = tagTypeOptionsSlice.reducer;
export const selectedTagTypeOptionReducer = selectedTagTypeOptionSlice.reducer;
export const campaignLevelTotalSpendReducer = campaignLevelTotalSpendSlice.reducer;
export const tagTypeLevelTotalSpendReducer = tagTypeLevelTotalSpendSlice.reducer;
export const funnelResponseReducer = funnelResponseSlice.reducer;
export const productMappingDataReducer = productMappingDataSlice.reducer;
export const generateMediaPlanDataReducer = generateMediaPlanDataSlice.reducer;
export const mediaPlanGridApiMapReducer = mediaPlanGridApiMapSlice.reducer;
export const mediaPlanningListReducer = mediaPlanningListSlice.reducer;
export const mediaPlanBudgetReducer = mediaPlanBudgetSlice.reducer;
export const mediaPlanEditConfigReducer = mediaPlanEditConfigSlice.reducer;
export const isMediaPlanEditReducer = isMediaPlanEditSlice.reducer;
export const isMediaPlanCreateReducer = isMediaPlanCreateSlice.reducer;
export const showMediaPlanChangeLogsReducer = showMediaPlanChangeLogsSlice.reducer;